import Layout from "../components/layout/Layout";
import SEO, { breadcrumbSchema, faqSchema, orgSchema } from "../components/layout/SEO";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Reveal } from "../components/shared/Reveal";
import CaseReviewForm from "../components/shared/CaseReviewForm";
import FAQAccordion from "../components/shared/FAQAccordion";
import LangLink from "../components/shared/LangLink";
import { Check, X, ArrowUpRight, AlertTriangle } from "lucide-react";
import { SITE } from "../data/site";
import { useI18n } from "../i18n";

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-4 border-b border-border">
    <span className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold font-sans sm:w-56 shrink-0">{label}</span>
    <span className="text-[15px] text-secondary-foreground">{value}</span>
  </div>
);

const Block = ({ title, children }) => (
  <Reveal className="mb-12">
    <h2 className="font-serif text-3xl md:text-4xl text-primary mb-5 tracking-tight">{title}</h2>
    {children}
  </Reveal>
);

// Reusable procedure-page template. Renders from a procedure data object.
export default function ProcedureTemplate({ data, faqs = [], related = [] }) {
  const { t, lang } = useI18n();
  const noindex = lang !== "en" || !!data.isPlaceholder;

  const procFaqs = faqs.length ? faqs : [
    { q: `Is ${data.h1.replace(" in Vietnam", "")} suitable for me?`, a: "Suitability can only be confirmed by a licensed surgeon after an in-person examination. An initial review helps explain the appropriate next steps." },
    { q: "How long should I stay in Vietnam?", a: data.stayInVietnam },
    { q: "What does the cost depend on?", a: `Cost depends on several factors, including ${data.costFactors.join(", ").toLowerCase()}. We do not publish fixed prices.` },
  ];

  return (
    <Layout>
      <SEO
        title={`${data.h1} | ${SITE.name}`}
        description={data.summary}
        path={`/${data.slug}`}
        noindex={noindex}
        type="article"
        schema={[
          orgSchema(),
          breadcrumbSchema([
            { name: "Home", to: "/" },
            { name: "Procedures", to: "/procedures" },
            ...(data.categorySlug ? [{ name: data.categoryLabel || "Nose Surgery", to: `/${data.categorySlug}` }] : []),
            { name: data.h1, to: `/${data.slug}` },
          ]),
          faqSchema(procFaqs),
        ]}
      />

      <Breadcrumbs items={[
        { name: t("nav.procedures"), to: "/procedures" },
        ...(data.categorySlug ? [{ name: data.categoryLabel || "Nose Surgery", to: `/${data.categorySlug}` }] : []),
        { name: data.h1 },
      ]} />

      <article className="container-psv py-12 md:py-16">
        <Reveal>
          <span className="eyebrow mb-4 block">{t("nav.procedures")}</span>
          <h1 className="font-serif font-medium text-primary text-5xl md:text-6xl leading-[1.0] tracking-tight max-w-4xl">{data.h1}</h1>
          <p className="mt-6 text-xl text-secondary-foreground font-light leading-relaxed max-w-3xl">{data.summary}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main content */}
          <div className="lg:col-span-7 xl:col-span-8">
            <Block title="What the procedure involves"><p className="text-[17px] text-secondary-foreground leading-relaxed">{data.involves}</p></Block>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <Reveal>
                <h3 className="font-serif text-2xl text-primary mb-4">Who may be assessed</h3>
                <ul className="space-y-2.5">
                  {data.suitable.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-[15px] text-secondary-foreground"><Check className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />{s}</li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.1}>
                <h3 className="font-serif text-2xl text-primary mb-4">Who may not be suitable</h3>
                <ul className="space-y-2.5">
                  {data.notSuitable.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-[15px] text-secondary-foreground"><X className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.5} />{s}</li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <Block title="Treatment techniques">
              <div className="flex flex-wrap gap-2.5">
                {data.techniques.map((tech) => (
                  <span key={tech} className="px-4 py-2 rounded-full border border-border text-sm text-primary/80 bg-surface">{tech}</span>
                ))}
              </div>
            </Block>

            <Block title="Questions to ask the surgeon">
              <ul className="space-y-3">
                {data.questions.map((q, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px] text-secondary-foreground"><span className="font-serif text-accent text-lg leading-none">{i + 1}.</span>{q}</li>
                ))}
              </ul>
            </Block>

            <Block title="Risks and limitations">
              <div className="flex items-start gap-3 bg-surface border-l-2 border-accent rounded-r-lg p-6">
                <AlertTriangle className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-[15px] text-secondary-foreground leading-relaxed">{data.risks}</p>
              </div>
            </Block>

            <Block title="Realistic outcome guidance"><p className="text-[17px] text-secondary-foreground leading-relaxed">{data.outcome}</p></Block>

            <Block title="What to expect">
              <div className="border-t border-border">
                <InfoRow label="Preparation" value={data.preparation} />
                <InfoRow label="Anaesthesia" value={data.anaesthesia} />
                <InfoRow label="Procedure duration" value={data.duration} />
                <InfoRow label="Hospital stay" value={data.hospitalStay} />
                <InfoRow label="Recovery timeline" value={data.recovery} />
                <InfoRow label="Time to stay in Vietnam" value={data.stayInVietnam} />
                <InfoRow label="Flying-home considerations" value={data.flyingHome} />
              </div>
            </Block>

            <Block title="Indicative cost factors">
              <p className="text-[15px] text-muted-foreground mb-4">Cost depends on your individual case. We do not publish fixed prices. Factors may include:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {data.costFactors.map((c) => (
                  <li key={c} className="flex items-center gap-2.5 text-[15px] text-secondary-foreground"><span className="w-1.5 h-1.5 rounded-full bg-accent" />{c}</li>
                ))}
              </ul>
            </Block>

            <Block title="Before-and-after & patient stories">
              <div className="bg-surface border-l-2 border-accent rounded-r-lg p-6">
                <p className="text-[15px] text-secondary-foreground leading-relaxed">
                  Before-and-after images and patient stories will be published here only with patient consent and appropriate clinical context. We do not digitally alter clinical results, and individual results vary.
                </p>
              </div>
            </Block>

            <Block title="Frequently asked questions"><FAQAccordion faqs={procFaqs} testIdPrefix="procedure-faq" /></Block>

            <Reveal className="bg-surface border border-border rounded-lg p-6 text-[13px] text-muted-foreground leading-relaxed">
              <strong className="text-primary font-semibold">Medical disclaimer:</strong> This page is general information and not medical advice. Suitability, risks and outcomes can only be assessed by a licensed medical professional after an in-person examination.
            </Reveal>
          </div>

          {/* Sidebar: case review form + related links */}
          <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-28 space-y-8">
            <div>
              <h2 className="font-serif text-2xl text-primary mb-4">{t("form.heading")}</h2>
              <CaseReviewForm defaultType="firstTime" />
            </div>

            {related.length > 0 && (
              <div className="bg-surface border border-border rounded-lg p-7">
                <h3 className="font-sans text-sm uppercase tracking-[0.14em] text-muted-foreground mb-4">Related</h3>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.to}>
                      <LangLink to={r.to} className="group flex items-center justify-between gap-2 text-[15px] text-primary hover:text-accent transition-colors">
                        {r.title} <ArrowUpRight className="w-4 h-4 opacity-50 group-hover:opacity-100" />
                      </LangLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </article>
    </Layout>
  );
}
